/**
 * Card-style resolution (classic vs inverted).
 *
 * `cardStyle()` is a per-viewer, client-side play-test lever: `?cardStyle=inverted|classic`
 * sets an explicit, persisted override; otherwise a stored `localStorage.cardStyle` wins;
 * default classic. Mirrors `isDevMode()` (dev-mode.js) and `isLearningMode()` (hints.js). The
 * dev overlay flips `localStorage.cardStyle` and reloads.
 *
 * `effectiveCardStyle()` is what `createCard`'s gate and the template cache actually read: the
 * lever still forces inverted everywhere when set, but its "classic" default no longer means
 * "always classic" — a Null round (the game's own inverted-colors identity, see
 * docs/components/CARDS.md) inverts ambiently, with no per-call-site pin needed. Snapshot-
 * correct throughout the boundary: the dispatch loop commits each event's state before running
 * its mutation (`animation/dispatch.js#applyEvent`), so this always answers from the committed
 * gameType, never a stale one.
 */
import { getCurrentState } from "./state.js";

const STYLES = ["classic", "inverted"];

export function cardStyle() {
  const param = new URLSearchParams(location.search).get("cardStyle");
  if (STYLES.includes(param)) {
    localStorage.setItem("cardStyle", param); // explicit override persists
    return param;
  }
  const stored = localStorage.getItem("cardStyle");
  if (stored != null && STYLES.includes(stored)) return stored;
  return "classic";
}

// The ambient default: the committed game state's style. The dev flag still forces inverted
// globally; otherwise a Null round inverts — with one narrow pre-boundary exception. A human
// Hand-Null declarer with a legal ouvert choice pauses at AWAIT_OUVERT_DECISION with gameType
// already committed server-side, but declaration-reveal.js's boundary wash — the moment the
// hand is MEANT to visibly invert — hasn't played yet (GameTypeDeclared, the event that carries
// it, only fires once the ouvert/hand choice resolves). Answering "inverted" here would spoiler
// a reload landing exactly on that pause, and would hand the wash an already-inverted source, so
// its reveal degenerates to the dev-flag's no-visible-change case for what is a common path, not
// a corner one — so this one pendingAction stays classic until the boundary retrofits it.
export function effectiveCardStyle() {
  if (cardStyle() === "inverted") return "inverted";
  const s = getCurrentState();
  if (s?.gameType !== "NULL") return "classic";
  return s.pendingAction === "AWAIT_OUVERT_DECISION" ? "classic" : "inverted";
}
